"use client";

import Image from "next/image";
import { motion } from "motion/react";
import { useEffect } from "react";
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  Check,
  Headphones,
  Lock,
  ShieldCheck,
  Sparkles,
  Star,
  UserRound,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import { reactWebAuthHref } from "@/lib/reactWebAuthUrl";

/** DroneJob.ai brand tokens — Flight Blue / Rotor Orange (brand guidelines). */
const FLIGHT_BLUE = {
  125: "#0A2B47",
  100: "#0F3A5F",
  75: "#1A4F7A",
  50: "#5C82A3",
  25: "#CFDDE9",
  10: "#EEF4F9",
} as const;

const ROTOR_ORANGE = {
  125: "#E2531F",
  100: "#FF6B35",
  75: "#FF8A5E",
  25: "#FFE1D4",
  10: "#FFF3ED",
} as const;

const SOLUTION_GREEN = {
  100: "#15803D",
  25: "#DCFCE7",
  10: "#F0FDF4",
} as const;

const SOLUTION_PURPLE = {
  100: "#7E22CE",
  25: "#F3E8FF",
  10: "#FAF5FF",
} as const;

const GREY = {
  100: "#717182",
  25: "#ECECF0",
  10: "#F6F6F8",
} as const;

type PersonaTone = "flight" | "sky" | "deck" | "rotor";

type PersonaCard = Readonly<{
  id: string;
  tone: PersonaTone;
  imageSrc: string;
  imageAlt: string;
  title: string;
  tagline: string;
  features: readonly string[];
  aiEnables: string;
  cta: string;
  ctaHref: string;
  external?: boolean;
}>;

type TrustItem = Readonly<{
  icon: "shield" | "star" | "lock" | "support";
  text: string;
  href?: string;
}>;

const toneStyles: Record<
  PersonaTone,
  Readonly<{
    accent: string;
    accentSoft: string;
    check: string;
    aiBoxBg: string;
    aiBoxFg: string;
    buttonBg: string;
    Icon: LucideIcon;
  }>
> = {
  flight: {
    accent: FLIGHT_BLUE[75],
    accentSoft: FLIGHT_BLUE[100],
    check: FLIGHT_BLUE[100],
    aiBoxBg: FLIGHT_BLUE[10],
    aiBoxFg: FLIGHT_BLUE[125],
    buttonBg: FLIGHT_BLUE[100],
    Icon: UserRound,
  },
  sky: {
    accent: SOLUTION_GREEN[100],
    accentSoft: SOLUTION_GREEN[100],
    check: SOLUTION_GREEN[100],
    aiBoxBg: SOLUTION_GREEN[10],
    aiBoxFg: FLIGHT_BLUE[125],
    buttonBg: SOLUTION_GREEN[100],
    Icon: BriefcaseBusiness,
  },
  deck: {
    accent: SOLUTION_PURPLE[100],
    accentSoft: SOLUTION_PURPLE[100],
    check: SOLUTION_PURPLE[100],
    aiBoxBg: SOLUTION_PURPLE[10],
    aiBoxFg: FLIGHT_BLUE[125],
    buttonBg: SOLUTION_PURPLE[100],
    Icon: UsersRound,
  },
  rotor: {
    accent: ROTOR_ORANGE[125],
    accentSoft: ROTOR_ORANGE[100],
    check: ROTOR_ORANGE[100],
    aiBoxBg: ROTOR_ORANGE[10],
    aiBoxFg: FLIGHT_BLUE[125],
    buttonBg: ROTOR_ORANGE[100],
    Icon: Building2,
  },
};

const trustIcons: Record<TrustItem["icon"], LucideIcon> = {
  shield: ShieldCheck,
  star: Star,
  lock: Lock,
  support: Headphones,
};

const FALLBACK_IMAGES: Record<string, string> = {
  pilots: "/home/personas/pilot-hd.jpg",
  businesses: "/home/personas/business-hd.jpg",
  "drone-businesses": "/home/personas/drone-business-hd.jpg",
  enterprise: "/home/personas/enterprise-hd.jpg",
};

const IMAGE_FOCAL_POINTS: Record<string, string> = {
  pilots: "38% 44%",
  businesses: "56% 40%",
  "drone-businesses": "56% 38%",
  enterprise: "38% 42%",
};

const PERSONA_CTA_VIEWPORT_GAP = 20;

function useFloatingPersonaCtas() {
  useEffect(() => {
    const cards = Array.from(
      document.querySelectorAll<HTMLElement>("[data-persona-card]"),
    );
    let animationFrame = 0;
    let lastScrollY = window.scrollY;
    let scrollDirection: "down" | "up" | null =
      window.scrollY > 0 ? "down" : null;

    const updatePositions = () => {
      animationFrame = 0;
      const nextScrollY = window.scrollY;
      const scrollDelta = nextScrollY - lastScrollY;

      if (nextScrollY > lastScrollY + 0.5) {
        scrollDirection = "down";
      } else if (nextScrollY < lastScrollY - 0.5) {
        scrollDirection = "up";
      }

      cards.forEach((card) => {
        const cta = card.querySelector<HTMLElement>("[data-persona-cta]");
        const dock = card.querySelector<HTMLElement>("[data-persona-cta-dock]");
        if (!cta || !dock) return;

        const cardRect = card.getBoundingClientRect();
        const ctaRect = cta.getBoundingClientRect();
        const maximumOffset = Math.max(0, dock.offsetTop - cta.offsetTop);
        const downwardFloatingBoundary = window.innerHeight * (2 / 3);
        const isCtaVisible =
          ctaRect.bottom > 0 && ctaRect.top < window.innerHeight;
        let offset = Number.parseFloat(cta.dataset.personaCtaOffset ?? "0");

        if (!Number.isFinite(offset)) offset = 0;
        offset = Math.min(maximumOffset, Math.max(0, offset));

        if (scrollDirection === "down") {
          if (cardRect.bottom <= 0) {
            offset = maximumOffset;
          } else if (
            offset <= 0.5 &&
            isCtaVisible &&
            ctaRect.top < downwardFloatingBoundary
          ) {
            offset += downwardFloatingBoundary - ctaRect.top;
          } else if (offset < maximumOffset && offset > 0) {
            offset += Math.max(0, scrollDelta);
          }
        } else if (scrollDirection === "up") {
          const bottomBoundary =
            window.innerHeight - PERSONA_CTA_VIEWPORT_GAP - ctaRect.height;

          if (cardRect.top >= window.innerHeight - PERSONA_CTA_VIEWPORT_GAP) {
            offset = 0;
          } else if (
            offset >= maximumOffset - 0.5 &&
            cardRect.bottom <= window.innerHeight
          ) {
            offset = maximumOffset;
          } else if (offset >= maximumOffset - 0.5) {
            offset -= ctaRect.top - bottomBoundary;
          } else if (offset > 0) {
            offset += Math.min(0, scrollDelta);
          }
        } else if (cardRect.bottom <= 0) {
          offset = maximumOffset;
        } else if (
          cardRect.top >=
          window.innerHeight - PERSONA_CTA_VIEWPORT_GAP
        ) {
          offset = 0;
        }

        const constrainedOffset = Math.min(maximumOffset, Math.max(0, offset));
        cta.dataset.personaCtaOffset = String(constrainedOffset);
        cta.style.transform = `translate3d(0, ${constrainedOffset}px, 0)`;
      });

      lastScrollY = nextScrollY;
    };

    const scheduleUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(updatePositions);
    };

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    cards.forEach((card) => resizeObserver.observe(card));
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    scheduleUpdate();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver.disconnect();
      cards.forEach((card) => {
        const cta = card.querySelector<HTMLElement>("[data-persona-cta]");
        if (!cta) return;
        cta.style.removeProperty("transform");
        delete cta.dataset.personaCtaOffset;
      });
    };
  }, []);
}

export default function PersonasSection() {
  const t = useTranslations("Personas");
  const locale = useLocale();
  const signupHref = reactWebAuthHref(locale, "signup");
  useFloatingPersonaCtas();

  const cards = ((t.raw("cards") as PersonaCard[]) ?? []).map((card) => ({
    ...card,
    imageSrc: FALLBACK_IMAGES[card.id] ?? card.imageSrc,
    ctaHref: card.ctaHref === "/signup" ? signupHref : card.ctaHref,
    external: card.ctaHref === "/signup",
  }));
  const trustItems = (t.raw("trustItems") as TrustItem[]) ?? [];

  return (
    <section
      className="bg-white py-16 sm:py-20"
      data-component-instance="homePersonasSection"
      style={{ backgroundColor: "#FFFFFF" }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-3xl text-center sm:mb-14">
          <p
            className="mb-3 text-xs font-bold tracking-[0.14em] uppercase"
            style={{ color: FLIGHT_BLUE[75] }}
          >
            {t("eyebrow")}
          </p>
          <h2
            className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
            style={{ color: FLIGHT_BLUE[125] }}
          >
            {t("title")}
          </h2>
          <p
            className="mx-auto mb-5 max-w-2xl text-base sm:text-lg"
            style={{ color: GREY[100] }}
          >
            {t("subtitle")}
          </p>
          <div
            className="mx-auto inline-flex max-w-2xl items-start gap-2 rounded-full px-4 py-2 text-left text-sm ring-1"
            style={{
              backgroundColor: ROTOR_ORANGE[10],
              color: FLIGHT_BLUE[125],
              borderColor: ROTOR_ORANGE[25],
              boxShadow: `0 0 0 1px ${ROTOR_ORANGE[25]}`,
            }}
          >
            <Sparkles
              className="mt-0.5 h-4 w-4 shrink-0"
              style={{ color: ROTOR_ORANGE[100] }}
              aria-hidden
            />
            <span>{t("aiBadge")}</span>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {cards.map((card, index) => {
            const styles = toneStyles[card.tone] ?? toneStyles.flight;
            const Icon = styles.Icon;
            return (
              <motion.article
                key={card.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                data-persona-card
                className="flex h-full flex-col overflow-clip rounded-2xl border bg-white"
                style={{
                  borderColor: GREY[25],
                  boxShadow: `0 8px 24px rgba(15, 58, 95, 0.12)`,
                }}
              >
                <div
                  className="relative aspect-[16/9] overflow-hidden"
                  style={{ backgroundColor: FLIGHT_BLUE[10] }}
                >
                  <Image
                    src={card.imageSrc}
                    alt={card.imageAlt}
                    fill
                    className="object-cover"
                    style={{
                      objectPosition: IMAGE_FOCAL_POINTS[card.id] ?? "center",
                    }}
                    sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw"
                    unoptimized
                  />
                  <div
                    className="absolute bottom-4 left-5 flex h-12 w-12 items-center justify-center rounded-full text-white ring-4 ring-white/95"
                    style={{
                      backgroundColor: styles.accentSoft,
                      boxShadow: `0 4px 12px rgba(15, 58, 95, 0.2)`,
                    }}
                    aria-hidden
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                </div>

                <div className="relative flex flex-1 flex-col px-5 pb-5 pt-8">
                  <h3
                    className="mb-1 text-xl font-bold"
                    style={{ color: FLIGHT_BLUE[125] }}
                  >
                    {card.title}
                  </h3>
                  <div
                    className="relative z-20 -mx-1 mt-3 bg-white/95 px-1 pt-2 backdrop-blur-sm will-change-transform"
                    data-persona-cta
                  >
                    <Button
                      asChild
                      className="w-full border-0 text-white shadow-none hover:opacity-90"
                      style={{ backgroundColor: styles.buttonBg }}
                    >
                      {card.external ? (
                        <a href={card.ctaHref}>
                          {card.cta}
                          <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
                        </a>
                      ) : (
                        <Link href={card.ctaHref}>
                          {card.cta}
                          <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
                        </Link>
                      )}
                    </Button>
                  </div>
                  <p
                    className="mb-4 mt-4 text-sm font-semibold"
                    style={{ color: styles.accent }}
                  >
                    {card.tagline}
                  </p>
                  <ul className="mb-4 space-y-2">
                    {card.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-2 text-sm"
                        style={{ color: GREY[100] }}
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 shrink-0"
                          style={{ color: styles.check }}
                          aria-hidden
                        />
                        <span style={{ color: FLIGHT_BLUE[125] }}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div
                    className="mb-5 rounded-xl px-3 py-2.5 text-xs leading-relaxed"
                    style={{
                      backgroundColor: styles.aiBoxBg,
                      color: styles.aiBoxFg,
                    }}
                  >
                    <span className="font-bold">{t("aiEnablesLabel")} </span>
                    {card.aiEnables}
                  </div>
                  <div
                    aria-hidden
                    className="mt-auto h-11 shrink-0"
                    data-persona-cta-dock
                  />
                </div>
              </motion.article>
            );
          })}
        </div>

        {trustItems.length > 0 ? (
          <div
            className="mt-10 grid gap-4 rounded-xl px-4 py-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:px-0"
            style={{
              backgroundColor: FLIGHT_BLUE[10],
              border: `1px solid ${FLIGHT_BLUE[25]}`,
            }}
          >
            {trustItems.map((item) => {
              const TrustIcon = trustIcons[item.icon] ?? ShieldCheck;
              const content = (
                <>
                  <TrustIcon
                    className="mt-0.5 h-5 w-5 shrink-0"
                    style={{ color: FLIGHT_BLUE[100] }}
                    aria-hidden
                  />
                  <p
                    className="text-sm leading-snug"
                    style={{ color: FLIGHT_BLUE[100] }}
                  >
                    {item.text}
                  </p>
                  {item.href ? (
                    <ArrowRight
                      className="mt-0.5 h-4 w-4 shrink-0 opacity-70 transition-transform group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  ) : null}
                </>
              );

              const className =
                "flex items-start gap-3 rounded-lg px-3 py-2 lg:px-5";

              return item.href ? (
                <Link
                  key={item.text}
                  href={item.href}
                  className={`group ${className} transition hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50`}
                  style={{ borderColor: FLIGHT_BLUE[25] }}
                >
                  {content}
                </Link>
              ) : (
                <div
                  key={item.text}
                  className={className}
                  style={{ borderColor: FLIGHT_BLUE[25] }}
                >
                  {content}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}
